import { useState } from 'react';
import { useAccountsCrumb } from './lib/usePhoneCrumb';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button, Card, Chip, DataTable, Pane, PaneHead, SubTabs } from '@/components/kit';
import type { TableRow } from '@/components/kit';
import { useNarrowViewport, useShellChrome } from '@/layouts/shellChrome';
import { useAuthStore } from '@/stores/useAuthStore';
import { Permission } from '@/types/user';
import { usePageMeta } from '@/hooks/usePageMeta';
import { PeriodRow } from './components/PeriodRow';
import { exportRows } from './lib/exportCsv';
import { exportStatementsPdf } from './lib/exportPdf';
import { useTenantStore } from '@/stores/useTenantStore';
import {
  DEAL_LABEL,
  amount,
  dayLabel,
  money,
  monthName,
  thisPeriod,
  useAccountsSummary,
  usePayrollActions,
  useStatement,
  type SlipContext,
  type StatementLine,
} from './lib/useAccounts';
import { AccountantMenu } from './AccountantMenu';
import { AdjustModal } from './components/AdjustModal';
import { AdvanceModal } from './components/AdvanceModal';

/**
 * Statement — `g_stmt` (7), `g_stmtline` (7d) and the payslip `g_slip` (7b).
 *
 * A STATEMENT LINE IS NEVER A BARE NUMBER. It names the bills it came from,
 * so a doctor who disputes it can be shown them — that is the difference
 * between a payout somebody accepts and one they argue with. Picking a line
 * opens the bills behind it, by number, with how the money was taken.
 *
 * INSURANCE APPEARS ON ITS PAYMENT DATE, not the visit's, carrying the
 * remittance reference: a claim paid in August for a July visit is August's
 * money, and the reference is how it is checked against the insurer's own
 * statement.
 *
 * A SALARIED PERSON GETS A PAYSLIP INSTEAD. There is no "collected" behind a
 * salary, so the same screen reads as lines of a slip — basic, then every
 * addition and deduction, then the net.
 */

export function StatementPage() {
  const go = useNavigate();
  const { personId = '' } = useParams();
  const [params] = useSearchParams();
  const [period, setPeriod] = useState(params.get('period') || thisPeriod());
  const [pickedId, setPicked] = useState<string | null>(null);
  const [paneTab, setPaneTab] = useState<'deductions' | 'line'>('deductions');
  const [adjusting, setAdjusting] = useState(false);
  const [lending, setLending] = useState(false);
  const [query, setQuery] = useState('');
  const narrow = useNarrowViewport();
  const canWrite = useAuthStore((st) => st.roleCheck.hasPermission(Permission.PAYOUT_WRITE));
  const act = usePayrollActions();

  const { data, isLoading, isError } = useStatement(personId, period);
  const { data: summary } = useAccountsSummary(period);

  usePageMeta({ title: data?.personName ?? 'Statement' });

  const salaried =
    data?.dealKind === 'salary' ||
    data?.dealKind === 'commission' ||
    (data?.context.slip?.basicMinor ?? 0) > 0;
  /* Two lines can fall on one day — the row id carries the line's place in the day. */
  const picked = (data?.lines ?? []).find((l, i) => `${l.date}#${i}` === pickedId) ?? null;
  const canAdjust = canWrite && !salaried && data?.runStatus === 'draft';
  const canKey = canWrite && data?.runStatus === 'draft';

  const sendToHer = () =>
    act.sendPayslip.mutate(
      { personId, period },
      {
        onSuccess: (r) =>
          r.delivered
            ? toast.success(`Payslip sent to ${r.to}`)
            : toast.error(
                r.reason === 'no-address'
                  ? `${data?.personName ?? 'They'} has no email address on file.`
                  : 'Mail is switched off for this clinic, so nothing was sent.',
              ),
        onError: () => toast.error('The payslip was not sent.'),
      },
    );

  /* THE FROZEN FIGURE AND THE LIVE ONE. Once a run is sent its gross stops
     moving, but money keeps arriving — the gap is what rolls to next month. */
  /* Only a SHARE deal earns from the lines below. A salary, a per-visit fee or a
     room rent is not made of collections, so comparing it to them reads as
     money missing when none is. */
  const drift =
    data && data.runStatus !== 'draft' && (data.dealKind === 'percent' || data.dealKind === 'commission')
      ? data.linesEarnedMinor - data.grossMinor
      : 0;

  const clinic = useTenantStore((st) => st.publicConfig)?.displayName ?? 'Clinic';
  const pdfStatement = () => {
    if (!data) return;
    exportStatementsPdf(`statement-${data.personName}-${period}`, clinic, `Statement — ${monthName(period)} ${period.slice(0, 4)}`, [
      {
        name: data.personName,
        deal: data.dealSummary ?? DEAL_LABEL[data.dealKind],
        columns: ['Date', 'Source', 'Collected', 'Share', 'Earned'],
        rows: data.lines.map((x) => [
          x.date,
          x.source,
          amount(x.collectedMinor),
          x.sharePercent === null ? '' : `${x.sharePercent}%`,
          amount(x.earnedMinor),
        ]),
        totals: [
          ['Gross', amount(data.grossMinor)],
          ['Deductions', amount(data.deductionsMinor)],
          ['Additions', amount(data.additionsMinor)],
          ['Net', amount(data.netMinor)],
        ],
      },
    ]);
  };

  const crumb = useAccountsCrumb('People we pay', data?.personName ?? 'Statement', '/accountant/people');
  useShellChrome(
    {
      raw: true,
      ...crumb,
      /* Phone: the pick is its own screen; back clears it. */
      paneOpen: !!picked,
      onPaneClose: () => setPicked(null),
      phoneMenu: (close) => <AccountantMenu door="people" onClose={close} />,
      title: data?.personName ?? 'Statement',
      sub: (
        <>
          <button
            type="button"
            className="lnk w6 brand"
            style={{ background: 'none', border: 0, padding: 0, font: 'inherit', cursor: 'pointer' }}
            onClick={() => go('/accountant/people')}
          >
            ‹ People we pay
          </button>{' '}
          ▸ {salaried ? 'payslip' : 'statement'} · {monthName(period)}
        </>
      ),
      search: salaried ? 'Search a line…' : 'Search a date, a bill…',
      searchValue: query,
      onSearchChange: setQuery,
      primary: {
        label: salaried ? '⬇ Payslip' : '⬇ Statement',
        disabled: !data,
        onClick: () =>
          exportRows(
            `${salaried ? 'payslip' : 'statement'}-${data?.personName ?? personId}-${period}`,
            salaried
              ? ['Line', 'Amount', 'Where it comes from']
              : ['Date', 'Source', 'Collected', 'Share', 'Earned', 'Bills'],
            salaried
              ? slipRows(data).map((r) => [r.label, (r.amountMinor / 100).toFixed(2), r.from])
              : (data?.lines ?? []).map((l) => [
                  l.date,
                  l.source,
                  (l.collectedMinor / 100).toFixed(2),
                  l.sharePercent === null ? '' : `${l.sharePercent}%`,
                  (l.earnedMinor / 100).toFixed(2),
                  l.bills.map((b) => b.number).join(' ') || (l.remittance ?? ''),
                ]),
          ),
      },
      pane: (
        <StatementPane
          data={data ?? null}
          salaried={salaried}
          line={picked}
          tab={picked ? paneTab : 'deductions'}
          onTab={setPaneTab}
          onAdjust={canAdjust ? () => setAdjusting(true) : undefined}
          onBills={() => go('/accountant/bills')}
          onSend={canWrite && salaried ? sendToHer : undefined}
          sending={act.sendPayslip.isPending}
          onLend={canKey ? () => setLending(true) : undefined}
        />
      ),
    },
    [data, picked, salaried, period, personId, pickedId, paneTab, canAdjust, query, canWrite, act.sendPayslip.isPending],
  );

  const earnedRows: TableRow[] = (data?.lines ?? [])
    .map((l, i) => ({ l, key: `${l.date}#${i}` }))
    .filter(({ l }) => !query.trim() || `${l.date} ${l.source} ${l.remittance ?? ''} ${l.bills.map((b) => b.number).join(' ')}`.toLowerCase().includes(query.trim().toLowerCase()))
    .map(({ l, key }) => ({
    id: key,
    cells: {
      date: <span className="mut">{dayLabel(l.date)}</span>,
      source: (
        <span>
          <span className="t-strong">{l.source}</span>
          {l.insured ? (
            <>
              {' '}
              <Chip kind="soft" small>{l.insured} insured</Chip>
            </>
          ) : null}
          {l.remittance ? (
            <>
              {' '}
              <Chip kind="success" small>remittance {l.remittance}</Chip>
            </>
          ) : null}
        </span>
      ),
      collected: <span className="t-strong num">{amount(l.collectedMinor)}</span>,
      share: (
        <span className="mut num">{l.sharePercent === null ? '—' : `${l.sharePercent}%`}</span>
      ),
      earned: <span className="t-strong num">{amount(l.earnedMinor)}</span>,
    },
  }));

  const slip = slipRows(data);
  const slipTable: TableRow[] = slip
    .filter((r) => !query.trim() || r.label.toLowerCase().includes(query.trim().toLowerCase()))
    .map((r, i) => ({
    id: `s${i}`,
    cells: {
      date: <span className="t-strong">{r.label}</span>,
      source:
        r.key === 'overtime' && canKey ? (
          <span className="t-sec mut">
            hours{' '}
            <input
              className="fld num"
              style={{ width: 72, display: 'inline-block' }}
              inputMode="decimal"
              aria-label="Overtime hours"
              defaultValue={data?.context.slip?.overtimeHours ?? 0}
              onBlur={(e) => {
                const hours = Number(e.target.value);
                if (!(hours >= 0) || hours === (data?.context.slip?.overtimeHours ?? 0)) return;
                act.setOvertime.mutate(
                  { personId, period, hours },
                  { onError: () => toast.error('The hours were not saved.') },
                );
              }}
            />{' '}
            · keyed in by you
          </span>
        ) : (
          <span className="t-sec mut">{r.from}</span>
        ),
      collected: (
        <span className={`t-strong num${r.amountMinor < 0 ? ' red' : ''}`}>
          {r.amountMinor < 0 ? '−' : ''}
          {amount(Math.abs(r.amountMinor))}
        </span>
      ),
    },
  }));

  return (
    <div className="maie desk acc-root">
      <PeriodRow period={period} onChange={setPeriod} branches={summary?.branches} />

      <div className="dcontent scroll">
        {salaried ? (
          <DataTable
            caption="The payslip"
            columns={[
              { key: 'date', label: 'Line', width: 46 },
              { key: 'collected', label: 'Amount', width: 18 },
              { key: 'source', label: 'Where it comes from', width: 36 },
            ]}
            rows={slipTable}
            empty={<div className="emptystate"><p className="t-sec mut">Nothing on the slip.</p></div>}
          />
        ) : (
          <DataTable
            caption={`Earned in ${monthName(period)}`}
            columns={[
              { key: 'date', label: 'Date', width: 13 },
              { key: 'source', label: 'Source', width: 46 },
              { key: 'collected', label: 'Collected', width: 15 },
              { key: 'share', label: 'Share', width: 12 },
              { key: 'earned', label: 'Earned', width: 14 },
            ]}
            rows={earnedRows}
            selectedId={pickedId}
            onSelect={(r) => {
              setPicked(r.id === pickedId ? null : r.id);
              setPaneTab('line');
            }}
            empty={
              <div className="emptystate">
                {isLoading ? (
                  <p className="t-sec mut">Reading the month…</p>
                ) : isError ? (
                  <p className="t-sec mut">
                    The statement did not come back. Nothing has been changed.
                  </p>
                ) : (
                  <>
                    <p className="es-t">No money against their name in {monthName(period)}</p>
                    <p className="t-sec mut">
                      Nothing was collected on a bill from one of their visits, and no claim of
                      theirs was remitted.
                    </p>
                  </>
                )}
              </div>
            }
          />
        )}

        <Card className="mt16">
          <div className="flex" style={{ gap: 12, alignItems: 'baseline' }}>
            <p className="t-strong grow">
              {salaried ? 'Net' : 'Gross earned'} — {monthName(period)}
            </p>
            <p className="t-sec mut">
              {data?.dealSummary ?? DEAL_LABEL[data?.dealKind ?? 'percent']}
            </p>
            <p className="t-title">
              {money(salaried ? (data?.netMinor ?? 0) : (data?.grossMinor ?? 0))}
            </p>
          </div>
          {!salaried && data ? (
            <p className="t-cap mut mt8">
              gross {amount(data.grossMinor)} − deductions {amount(data.deductionsMinor)} +
              additions {amount(data.additionsMinor)} = net {amount(data.netMinor)}
            </p>
          ) : null}
        </Card>

        {drift ? (
          <Card style={{ borderColor: 'var(--warning-600)' }}>
            <p className="t-strong amb">
              {amount(Math.abs(drift))} {drift > 0 ? 'more' : 'less'} has been collected since the
              run was sent
            </p>
            <p className="t-cap mut mt8">
              The figure above is what was APPROVED and does not move. Money that arrives after the
              cut-off rolls into the next period — the lines below show it because they are the
              evidence, not the total.
            </p>
          </Card>
        ) : null}

        {narrow ? (
          <>
            {salaried && data ? (
              <SlipPaneBody data={data} onLend={canKey ? () => setLending(true) : undefined} />
            ) : (
              <DeductionsBody data={data ?? null} salaried={salaried} />
            )}
            {salaried && canWrite ? (
              <Button kind="outline" style={{ marginTop: 12 }} disabled={act.sendPayslip.isPending} onClick={sendToHer}>
                Send to her
              </Button>
            ) : null}
            {canAdjust ? (
              <Button kind="outline" style={{ marginTop: 12 }} onClick={() => setAdjusting(true)}>
                + Add adjustment
              </Button>
            ) : null}
            {data ? (
              <Button kind="ghost" style={{ marginTop: 12 }} onClick={pdfStatement}>
                ⬇ PDF statement
              </Button>
            ) : null}
          </>
        ) : null}

        <p className="t-sec mut mt16">
          {salaried
            ? 'A salary has no collected money behind it, so the slip lists what makes it up instead — and every line says where it comes from.'
            : 'Every row traces to its bills — pick one and the pane shows them. Insurance money appears on its payment date, tied to the remittance reference.'}
        </p>
      </div>

      <AdvanceModal
        open={lending}
        who={data?.personName ?? ''}
        onClose={() => setLending(false)}
        onSave={(amountMinor, monthlyMinor, note) => {
          act.giveAdvance.mutate(
            { personId, amountMinor, monthlyMinor, note },
            {
              onSuccess: () => toast.success('Advance recorded'),
              onError: () => toast.error('It was not recorded.'),
            },
          );
          setLending(false);
        }}
      />

      <AdjustModal
        open={adjusting}
        who={data?.personName ?? ''}
        period={period}
        onClose={() => setAdjusting(false)}
        onSave={(kind, amountMinor, reason) => {
          act.addAdjustment.mutate(
            { doctorId: personId, period, kind, amountMinor, reason },
            {
              onSuccess: () => toast.success(`${kind === 'deduction' ? 'Deduction' : 'Addition'} added`),
              onError: () => toast.error('It was not added.'),
            },
          );
          setAdjusting(false);
        }}
      />
    </div>
  );
}

/** The payslip's lines — basic, then everything that moved it. */
function slipRows(
  data:
    | {
        netMinor: number;
        grossMinor: number;
        adjustments: Array<{ kind: string; label: string; amountMinor: number }>;
        context: SlipContext;
      }
    | null
    | undefined,
): Array<{ key: string; label: string; amountMinor: number; from: string }> {
  if (!data) return [];
  const sl = data.context.slip;
  const adj = data.adjustments.map((a, i) => ({
    key: `adj${i}`,
    label: a.label,
    amountMinor: a.kind === 'deduction' ? -a.amountMinor : a.amountMinor,
    from: `an adjustment on this month's run`,
  }));
  if (!sl) {
    return [
      { key: 'basic', label: 'Basic salary', amountMinor: data.grossMinor, from: 'their deal, set in Settings ▸ Money' },
      ...adj,
    ];
  }
  return [
    { key: 'basic', label: 'Basic salary', amountMinor: sl.basicMinor, from: 'the contract in Settings ▸ Money' },
    { key: 'overtime', label: `Overtime · ${sl.overtimeHours} h`, amountMinor: sl.overtimeMinor, from: 'hours keyed in for the month' },
    { key: 'absence', label: `Absence · ${sl.absenceDays} day${sl.absenceDays === 1 ? '' : 's'}`, amountMinor: -sl.absenceMinor, from: 'attendance the roster already records' },
    { key: 'advance', label: 'Advance repayment', amountMinor: -sl.advanceMinor, from: 'the running advance balance' },
    { key: 'social', label: 'Social insurance', amountMinor: -sl.socialInsuranceMinor, from: 'Settings ▸ Money' },
    ...adj,
  ];
}

/** Deductions & adjustments: every adjustment on the run, then the NET. */
function DeductionsBody({
  data,
  salaried,
}: {
  data: {
    netMinor: number;
    adjustments: Array<{ id: string; kind: string; label: string; amountMinor: number }>;
    context: SlipContext;
  } | null;
  salaried: boolean;
}) {
  const adv = data?.context.slip?.advanceMinor ?? 0;
  return (
    <>
      {!salaried && adv ? (
        <Card>
          <p className="t-ovl mut">Advance repayment</p>
          <p className="t-strong num mt4 red">−{amount(adv)}</p>
          <p className="t-cap mut mt4">deduction · off the running advance balance</p>
        </Card>
      ) : null}
      {(data?.adjustments ?? []).map((a) => (
        <Card key={a.id}>
          <p className="t-ovl mut">{a.label}</p>
          <p className={`t-strong num mt4 ${a.kind === 'deduction' ? 'red' : 'grn'}`}>
            {a.kind === 'deduction' ? '−' : '+'}
            {amount(a.amountMinor)}
          </p>
          <p className="t-cap mut mt4">{a.kind === 'deduction' ? 'deduction' : 'addition'} · on this month’s run</p>
        </Card>
      ))}
      {!data?.adjustments.length && !(!salaried && adv) ? (
        <Card>
          <p className="t-sec sec">Nothing was deducted or added — their net is their gross.</p>
        </Card>
      ) : null}
      <Card style={{ borderColor: 'var(--success-600)' }}>
        <div className="flex" style={{ alignItems: 'baseline' }}>
          <p className="t-strong grow">NET</p>
          <p className="t-title grn">{money(data?.netMinor ?? 0)}</p>
        </div>
      </Card>
      <Card kind="ghost">
        <p className="t-sec mut">
          {salaried
            ? 'Every deduction names its reason — a slip with an unexplained number starts an argument.'
            : 'Deductions come from records, not memory. A manual adjustment needs a reason and is logged.'}
        </p>
      </Card>
    </>
  );
}

/** The docked half of the document — `g_stmt`, `g_stmtline`, `g_slip`. */
function StatementPane({
  data,
  salaried,
  line,
  tab,
  onTab,
  onAdjust,
  onBills,
  onSend,
  sending,
  onLend,
}: {
  data: Parameters<typeof DeductionsBody>[0]['data'];
  salaried: boolean;
  line: StatementLine | null;
  tab: 'deductions' | 'line';
  onTab: (t: 'deductions' | 'line') => void;
  onAdjust?: () => void;
  onBills: () => void;
  onSend?: () => void;
  sending?: boolean;
  onLend?: () => void;
}) {
  const showLine = tab === 'line' && !!line;
  return (
    <Pane
      head={
        showLine && line ? (
          <PaneHead
            overline="Selected line"
            title={`${dayLabel(line.date)} · ${money(line.earnedMinor)}`}
            sub={
              line.sharePercent === null
                ? 'no share set'
                : `${line.sharePercent}% of ${amount(line.collectedMinor)}`
            }
            wrapTitle
          />
        ) : (
          <PaneHead
            overline={salaried ? 'This payslip' : 'This statement'}
            title={salaried ? 'Deductions & advances' : 'Deductions & adjustments'}
            sub="the second half of the same document"
          />
        )
      }
      foot={
        showLine ? (
          <Button kind="outline" style={{ width: '100%' }} onClick={onBills}>
            Open the bills behind it
          </Button>
        ) : salaried && onSend ? (
          <Button kind="outline" style={{ width: '100%' }} disabled={sending} onClick={onSend}>
            Send to her
          </Button>
        ) : onAdjust ? (
          <Button kind="outline" style={{ width: '100%' }} onClick={onAdjust}>
            + Add adjustment
          </Button>
        ) : undefined
      }
    >
      {!salaried ? (
        <SubTabs
          label="Statement pane"
          items={[
            { key: 'deductions' as const, label: 'Deductions' },
            { key: 'line' as const, label: 'Selected line' },
          ]}
          active={showLine ? 'line' : 'deductions'}
          onSelect={onTab}
        />
      ) : null}
      {showLine && line ? (
        <LineBody line={line} />
      ) : salaried && data ? (
        <SlipPaneBody data={data} onLend={onLend} />
      ) : (
        <DeductionsBody data={data} salaried={salaried} />
      )}
    </Pane>
  );
}

/** The payslip's pane: advance balance, absence, where they work. */
function SlipPaneBody({
  data,
  onLend,
}: {
  data: NonNullable<Parameters<typeof DeductionsBody>[0]['data']>;
  onLend?: () => void;
}) {
  const c = data.context;
  const sl = c.slip;
  const days = sl?.absenceDays ?? 0;
  return (
    <>
      <Card>
        <p className="t-ovl mut">Advance balance</p>
        {c.advanceBalanceMinor > 0 ? (
          <p className="t-sec sec mt4">
            {money(c.advanceBalanceMinor)} remaining
            {c.advanceMonthsLeft ? ` · ${c.advanceMonthsLeft} month${c.advanceMonthsLeft === 1 ? '' : 's'} to clear` : ''}
          </p>
        ) : (
          <p className="t-sec sec mt4">No advance outstanding</p>
        )}
        <p className="t-cap mut mt4">
          advances (سلف) are tracked as a running balance and deducted until cleared
        </p>
        {onLend ? (
          <p className="mt8">
            <button type="button" className="lnk w6 brand" style={{ background: 'none', border: 0, padding: 0, font: 'inherit', cursor: 'pointer' }} onClick={onLend}>
              + Record an advance
            </button>
          </p>
        ) : null}
      </Card>
      <Card>
        <p className="t-ovl mut">Absence</p>
        <p className="t-sec sec mt4">
          {days
            ? `${days} day${days === 1 ? '' : 's'} · ${c.absenceDates.map(dayLabel).join(' and ')} marked absent by the roster`
            : 'No days marked absent by the roster'}
        </p>
      </Card>
      <Card>
        <p className="t-ovl mut">Employed</p>
        <p className="t-sec sec mt4">
          {[
            c.branches.length ? c.branches.join(', ') : 'All branches',
            c.employedSince ? `since ${monthYear(c.employedSince)}` : null,
            c.roleName,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </Card>
      <Card kind="ghost">
        <p className="t-sec mut">
          Every deduction names its reason — a slip with an unexplained number starts an argument.
        </p>
      </Card>
    </>
  );
}

/** "Mar 2025" from a date. */
function monthYear(date: string): string {
  const d = new Date(`${date}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? date
    : d.toLocaleString('en-GB', { month: 'short', year: 'numeric' });
}

/** What is behind one line — the bills, by number. */
function LineBody({ line }: { line: StatementLine }) {
  const methods = new Map<string, number>();
  for (const b of line.bills) {
    const m = b.method ?? 'not recorded';
    methods.set(m, (methods.get(m) ?? 0) + 1);
  }

  return (
    <>
      <Card>
        <p className="t-ovl mut">What is behind it</p>
        <p className="t-sec sec mt4">
          {line.remittance
            ? `${line.insured} claim${line.insured === 1 ? '' : 's'} remitted`
            : `${line.visits} visit${line.visits === 1 ? '' : 's'}`}
          {methods.size
            ? ` · ${[...methods.entries()].map(([m, n]) => `${n} ${m}`).join(', ')}`
            : ''}
        </p>
        {line.insured && !line.remittance ? (
          <p className="t-cap mut mt8">
            {line.insured} of them insured — that money arrives on the claim's payment date, not
            today
          </p>
        ) : null}
      </Card>

      <Card>
        <p className="t-ovl mut">{line.remittance ? 'The remittance' : 'The bills'}</p>
        {line.remittance ? (
          <p className="t-sec sec mt4">{line.remittance}</p>
        ) : line.bills.length ? (
          <p className="t-sec sec mt4">{line.bills.map((b) => b.number).join(' · ')}</p>
        ) : (
          <p className="t-sec mut mt4">No bill numbers on this line.</p>
        )}
        <p className="t-cap mut mt8">
          {line.bills.length
            ? `${line.bills.length} bill${line.bills.length === 1 ? '' : 's'} — this is what a disputed line is answered with`
            : 'tied to the insurer’s own reference'}
        </p>
      </Card>

      <Card>
        <p className="t-ovl mut">Back to the whole statement</p>
        <p className="t-sec sec mt4">
          The Deductions tab holds the second half of the document — consumables, the advance,
          withholding and the NET
        </p>
      </Card>

      <Card kind="accent">
        <p className="t-sec brand">
          A statement line is never a bare number: it names the bills it came from, so a doctor who
          disputes it can be shown them.
        </p>
      </Card>
    </>
  );
}
